import { CommonModule } from '@angular/common';
import {
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbScrollSpy,
  NgbScrollSpyFragment,
  NgbDropdownModule,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import {
  Component,
  computed,
  inject,
  Injector,
  OnInit,
  signal,
  Signal,
  ViewChild,
} from '@angular/core';
import {
  CreateUpdateCustomerReturnRequestDto,
  CustomerDocDefaultValueDto,
  CustomerReturnRequestDto,
  CustomerReturnRequestService,
  CustomerReturnRequestStatus,
  DayBookDto,
  DayBookService,
  DocHierarchyDto,
} from '@digiwall/goliath-documents/proxy';
import { Observable } from 'rxjs';
import { CustomerService } from '@digiwall/goliath-contacts/proxy';
import { GoliathContactsEnumerationTypeStore, SalesPersonStore } from '@digiwall/goliath-contacts';
import {
  AnnexesListComponent,
  CurrentCompanyStore,
  GoliathCoreEnumerationTypeStore,
} from '@digiwall/goliath-core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfigStateService,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { FormBuilder, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { BreadcrumbService } from 'xng-breadcrumb';
import { ClassificationTypeService, DocRole, WeightUnit } from '@digiwall/goliath-core/proxy';
import { DocTemplateService } from '../../base-docs/detail/doc-template-selection/doc-template.service';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { ReportViewerComponent } from '@odyssey/reporting';
import { BaseCustomerDocDetail } from '../../base-customer-docs/base-customer-doc-detail';
import {
  ChatComponent,
  DeactivatableComponentService,
  DwInputRequiredDirective,
  HistoryComponent,
  OdysseyOffcanvas,
  OffcanvasSize,
} from '@digiwall/core';
import { DocReportPermission } from '@digiwall/goliath-documents/config';
import {
  CounterComponent,
  DropdownMenu,
  PanelComponent,
  DropdownMenuComponent,
} from '@digiwall/ui-kit';
import { DwGridDetailNavigationComponent, DwTabsComponent } from '@digiwall/ag-grid';
import { DwNavLinkDirective } from '@digiwall/core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DwKeyboardShortcutDirective } from '@digiwall/core';
import { DwScrollSpyDirective } from '@digiwall/ag-grid';
import { DocConditionsComponent } from '../../base-docs/detail/doc-conditions/doc-conditions.component';
import { DocClassificationsComponent } from '../../base-docs/detail/doc-classifications/doc-classifications.component';
import { DocCustomerAddressesComponent } from '../../base-docs/detail/doc-customer-addresses/doc-customer-addresses.component';
import { DocCustomerOthersComponent } from '../../base-docs/detail/doc-customer-others/doc-customer-others.component';
import { SidePanelComponent } from '@digiwall/ui-kit';
import { DialogBoxComponent } from '@digiwall/ui-kit';
import { DocSidePanelComponent } from '../../base-docs/detail/doc-ui-components/doc-side-panel/doc-side-panel.component';
import {
  DocumentCoreCardComponent,
  DocumentCustomerCardComponent,
  DocumentCustomerDateCardComponent,
  DocumentCustomerStatusCardComponent,
} from '../../../components';
import { StockValidationSharedLogicService } from '../../stock-validation-shared-logics/stock-validation-shared-logic.service';
import { CustomerReturnRequestLinesEditorComponent } from './customer-return-request-lines/customer-return-request-lines-editor.component';
import { NgSelectComponent } from '@ng-select/ng-select';
import { CustomerReturnNoteCreationComponent } from './customer-return-note-creation/customer-return-note-creation.component';
import { DocNumberTagComponent } from '../../../components/doc-number-tag/doc-number-tag.component';

@Component({
  selector: 'document-customer-return-request-detail',
  templateUrl: './customer-return-request-detail.component.html',
  styleUrl: './customer-return-request-detail.component.scss',
  providers: [DocTemplateService, StockValidationSharedLogicService],
  imports: [
    CommonModule,
    PanelComponent,
    DwTabsComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    DwNavLinkDirective,
    NgbNavContent,
    NgbScrollSpy,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    DwKeyboardShortcutDirective,
    NgbScrollSpyFragment,
    DwScrollSpyDirective,
    CustomerReturnRequestLinesEditorComponent,
    DocConditionsComponent,
    DocClassificationsComponent,
    DocCustomerAddressesComponent,
    DocCustomerOthersComponent,
    AnnexesListComponent,
    NgbDropdownModule,
    DropdownMenuComponent,
    NgbNavOutlet,
    SidePanelComponent,
    FormsModule,
    DialogBoxComponent,
    ReportViewerComponent,
    LocalizationModule,
    CounterComponent,
    ChatComponent,
    DwGridDetailNavigationComponent,
    DocSidePanelComponent,
    DocumentCoreCardComponent,
    DocumentCustomerCardComponent,
    DocumentCustomerDateCardComponent,
    DocumentCustomerStatusCardComponent,
    NgSelectComponent,
    DwInputRequiredDirective,
    HistoryComponent,
    DocNumberTagComponent,
  ],
})
export class CustomerReturnRequestDetailComponent
  extends BaseCustomerDocDetail<
    CustomerReturnRequestService,
    CustomerReturnRequestDto,
    CreateUpdateCustomerReturnRequestDto,
    CustomerDocDefaultValueDto
  >
  implements OnInit
{
  @ViewChild('customerReturnRequestLineGrid')
  docLineGrid: CustomerReturnRequestLinesEditorComponent;
  @ViewChild('report') reportViewer: ReportViewerComponent;

  docRole = DocRole.CustomerReturnRequest;
  fromPurchase: boolean = false;
  dayBook: DayBookDto;
  canAddPayment = signal(false);
  remainingAmount$: Observable<number>;

  currentCompanyStore = inject(CurrentCompanyStore);
  enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  contactEnumerationTypeStore = inject(GoliathContactsEnumerationTypeStore);
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);
  salesPersonStore = inject(SalesPersonStore);
  stockValidationSharedLogicService = inject(StockValidationSharedLogicService);

  protected saveActionBtns = computed(() => {
    let btns = [
      {
        label: this.localizationService.instant('::Save'),
        action: () => this.save(),
        disabled: () =>
          this.isValidated() &&
          (!this.form.dirty || !this.stockValidationSharedLogicService.canSave()),
      },
    ];
    return btns;
  });

  actions: Signal<DropdownMenu> = computed(() => {
    const baseGroup = this.baseActionsThirdPartyDoc().concat(
      {
        label: '::GeneratePdf',
        icon: 'digi-out-pdf-file',
        action: () => this.generatePdf(this.reportViewer),
      },
      {
        label: 'Doc::CustomerReturnRequest:CreateReturnNote',
        icon: 'digi-out-add-file',
        action: () => this.toReturnNote(),
        visible: computed(() => !this.isClosed()),
      },
      {
        label: 'Doc::PrintDocument',
        icon: 'digi-out-print',
        action: () => this.printDocument(),
        enabled: this.canPrintDocument,
      },
    );
    return {
      groups: [
        baseGroup,
        [
          {
            label: 'Doc::GenerateDocPrintTemplate',
            icon: 'digi-out-edit-square',
            action: () => this.openDesigner(),
            requiredPolicy: DocReportPermission.Create,
          },
        ],
      ],
    };
  });

  weightUnits = WeightUnit;
  totalAmount: number = 0;
  totalAmountVatIncluded: number = 0;

  override get defaultValue() {
    return Object.assign(this.baseDefaultValue, {
      isExported: false,
      allowPartialDelivery: false,
      customerReturnRequestStatus: CustomerReturnRequestStatus.Pending,
    });
  }

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    customerReturnRequestService: CustomerReturnRequestService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    injector: Injector,
    customerService: CustomerService,
    config: ConfigStateService,
    docTemplateService: DocTemplateService,
    classificationTypeService: ClassificationTypeService,
    confirmationService: ConfirmationService,
    deactivableComponentService: DeactivatableComponentService,
  ) {
    super(
      route,
      router,
      subscriptionService,
      customerReturnRequestService,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      docTemplateService,
      injector,
      customerService,
      config,
      confirmationService,
      deactivableComponentService,
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.stockValidationSharedLogicService.setup({
      isCreationMode: this.isCreationMode,
      isValidated: this.isValidated,
      lineCount: this.lineCount,
      isStockValidated: this.isStockValidated,
    });
    this.init();
  }

  override get formModel() {
    return Object.assign({}, super.formModel, {
      customerReturnRequestStatus: [null, Validators.required],
    });
  }

  getEntityCallback(): void {
    super.getEntityCallback();
    super.loadThirdParties(this.entity.isClosed);

    this.docLineGrid?.lineGrid?.grid?.grid?.api?.redrawRows();
  }

  protected createEntity(defaultValue) {
    defaultValue.isValidated = true;

    return defaultValue;
  }

  protected onHierarchyLoaded($event: DocHierarchyDto) {
    if ($event.previousDocs.findIndex(x => x.docRole == DocRole.CustomerSalesPurchase) > -1) {
      this.fromPurchase = true;
    }
  }

  protected override onThirdPartyChanged = (thirdPartyId: string): void => {
    super.onThirdPartyChanged(thirdPartyId);
    this.thirdPartyId$.next(thirdPartyId);
  };

  public override canDeactivate = async () => {
    this.canDeactivateTitle = this.stockValidationSharedLogicService.getCanDeactivateTitle();
    return !this.form.dirty;
  };

  private toReturnNote() {
    const canvaRef = this.offcanvasService.open(CustomerReturnNoteCreationComponent, null, {
      injector: this.injector,
      offcanvasPanelClass: OffcanvasSize.Small,
    });

    const component = canvaRef.componentInstance as CustomerReturnNoteCreationComponent;
    component.returnNoteRequest = this.entity;

    canvaRef.closed.subscribeOnce(returnNoteId => {
      if (!returnNoteId) return;

      this.router.navigate(['../../customer-return-notes', returnNoteId], {
        relativeTo: this.route,
      });
    });
  }
}
